import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button } from "react-bootstrap";
import { Paths } from "src/paths";
import { useCurrentPath, withReturnTo } from "src/shared/utils/returnTo";
import Loader from "src/components/common/loader/loader";
import { ContractList, ContractEditor, ContractFormModal } from "src/modules/contracts";
import { Organization } from "@models/organization.model";
import useOrganizationContracts from "../hooks/useOrganizationContracts";
import OrganizationInvoiceModal from "./OrganizationInvoiceModal";
import { CreateContractPayload } from "@services/finance/contracts/commands/cmdCreateContract";
import { UpdateContractPayload } from "@services/finance/contracts/commands/cmdUpdateContract";

interface Props {
  organizationId: number;
  organizations: Organization[];
}

const OrganizationContractsTab: FC<Props> = ({ organizationId, organizations }) => {
  const navigate = useNavigate();
  const here = useCurrentPath();
  const {
    contracts,
    selectedContractId,
    budgets,
    pricesheets,
    pricesheetItems,
    spendAccount,
    credits,
    debits,
    balance,
    isLoading,
    isSaving,
    error,
    selectContract,
    createContract,
    updateContract,
    deleteContract,
    createBudget,
    updateBudget,
    deleteBudget,
    savePricesheet,
    deletePricesheet,
    createCredit,
    updateCredit,
    deleteCredit,
    createDebit,
    updateDebit,
    deleteDebit,
  } = useOrganizationContracts(organizationId);

  const [showCreate, setShowCreate] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const selectedContract = contracts.find((c) => c.id === selectedContractId) ?? null;

  const orgName = (id: number) => organizations.find((o) => o.id === id)?.name ?? `Org #${id}`;

  const handleCreateContract = async (payload: CreateContractPayload | UpdateContractPayload) => {
    await createContract(payload as CreateContractPayload);
    setShowCreate(false);
  };

  if (isLoading && contracts.length === 0) {
    return <Loader />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">Contracts</h5>
          <p className="text-muted small mb-0">
            Manage contracts, budgets, and pricing for this organization.
          </p>
        </div>
        <Button variant="outline-primary" size="sm" onClick={() => setShowInvoiceModal(true)}>
          <i className="bi bi-receipt me-2" />
          View Invoice
        </Button>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <div className="row g-3" style={{ minHeight: 480 }}>
        <div className="col-12 col-lg-4">
          <ContractList
            contracts={contracts}
            selectedId={selectedContractId}
            onSelect={selectContract}
            onCreate={() => setShowCreate(true)}
            isBusy={isSaving}
            getLabel={(c) => orgName(c.service_provider_org_id)}
            emptyMessage="No contracts yet"
          />
        </div>

        <div className="col-12 col-lg-8">
          {!selectedContract ? (
            <div className="contract-empty-state">
              <i className="bi bi-file-earmark-text empty-icon" />
              <div className="empty-title">Select a contract</div>
              <div className="empty-subtitle">
                Choose a contract from the list, or create a new one.
              </div>
            </div>
          ) : (
            <ContractEditor
              contract={selectedContract}
              budgets={budgets}
              pricesheets={pricesheets}
              pricesheetItems={pricesheetItems}
              spendAccount={spendAccount}
              credits={credits}
              debits={debits}
              balance={balance}
              organizationId={organizationId}
              isSaving={isSaving}
              title={
                selectedContract.name?.trim() ||
                `Contract with ${orgName(selectedContract.service_provider_org_id)}`
              }
              mutations={{
                updateContract,
                deleteContract,
                createBudget,
                updateBudget,
                deleteBudget,
                savePricesheet,
                deletePricesheet,
                createCredit,
                updateCredit,
                deleteCredit,
                createDebit,
                updateDebit,
                deleteDebit,
              }}
              onEditPricesheet={(p) =>
                navigate(
                  withReturnTo(
                    `${import.meta.env.BASE_URL}${Paths.CONTRACT_PRICESHEET}`
                      .replace(":contractId", String(selectedContract.id))
                      .replace(":pricesheetId", String(p.id)),
                    here,
                  ),
                )
              }
            />
          )}
        </div>
      </div>

      {showCreate && (
        <ContractFormModal
          show
          onClose={() => setShowCreate(false)}
          onSave={handleCreateContract}
          contract={null}
          clientOrgId={organizationId}
          isSaving={isSaving}
        />
      )}

      {showInvoiceModal && (
        <OrganizationInvoiceModal
          show
          onClose={() => setShowInvoiceModal(false)}
          organizationId={organizationId}
          organizationName={orgName(organizationId)}
        />
      )}
    </div>
  );
};

export default OrganizationContractsTab;
